import { Injectable } from '@angular/core';
import { SETTINGS } from '../config/common.settings';
import { HttpClient } from '@angular/common/http';
import { User } from '../models/user.model';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private allUsers: User[] = [];
  private singleUser: User = {};
  loggedInUser: any = {};
  onLoggedInUserChange: Subject<any> = new BehaviorSubject({});

  constructor(private http: HttpClient) {}

  getAllUsers(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .post(`${SETTINGS.BASE_API}/admin/users/get-all`, payload)
        .subscribe({
          next: (response: any) => {
            this.allUsers = response[0];
            resolve(this.allUsers);
          },
          error: (error: any) => {
            reject(error);
          },
        });
    });
  }

  getUsersDropdown(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .get(`${SETTINGS.BASE_API}/admin/users/dropdown`)
        .subscribe({
          next: (response: any) => {
            resolve(response);
          },
          error: (error: any) => {
            reject(error);
          },
        });
    });
  }

  getUserByID(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${SETTINGS.BASE_API}/admin/user/${id}`).subscribe({
        next: (response: any) => {
          this.singleUser = response;
          resolve(this.singleUser);
        },
        error: (error: any) => {
          reject(error);
        },
      });
    });
  }

  getUserByIDToHeader(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .get(`${SETTINGS.BASE_API}/admin/user-to-header/${id}`)
        .subscribe({
          next: (response: any) => {
            this.singleUser = response;
            this.loggedInUser = response;
            this.onLoggedInUserChange.next(this.loggedInUser);
            resolve(this.singleUser);
          },
          error: (error: any) => {
            reject(error);
          },
        });
    });
  }

  addNewUser(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${SETTINGS.BASE_API}/admin/create`, payload).subscribe({
        next: (response: any) => {
          resolve(response);
        },
        error: (error: any) => {
          reject(error);
        },
      });
    });
  }

  editUser(payload: User): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .put(`${SETTINGS.BASE_API}/admin/user/update`, payload)
        .subscribe({
          next: (response: any) => {
            // Keep the cached loggedInUser in sync so the BehaviorSubject
            // replays the correct appLanguage on component re-initialisation
            // (e.g. after the browser back-button navigation).
            const anyPayload = payload as any;
            if (anyPayload.appLanguage && this.loggedInUser?.[0]) {
              this.loggedInUser[0].appLanguage = anyPayload.appLanguage;
              this.onLoggedInUserChange.next(this.loggedInUser);
            }
            resolve(response);
          },
          error: (error: any) => {
            reject(error);
          },
        });
    });
  }

  saveContentImagePreference(
    contentType: 'experiences' | 'destinations',
    contentId: string,
    imageUrl: string
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .put(`${SETTINGS.BASE_API}/admin/user/content-image-preference`, {
          contentType,
          contentId,
          imageUrl,
        })
        .subscribe({
          next: (response: any) => {
            resolve(response);
          },
          error: (error: any) => {
            reject(error);
          },
        });
    });
  }

  getContentImagePreference(
    contentType: 'experiences' | 'destinations',
    contentId: string
  ): Promise<{ imageUrl: string }> {
    return new Promise((resolve, reject) => {
      this.http
        .get<{ imageUrl: string }>(
          `${SETTINGS.BASE_API}/admin/user/content-image-preference`,
          { params: { contentType, contentId } }
        )
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error),
        });
    });
  }

  saveUserPreference(key: string, value: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .put(`${SETTINGS.BASE_API}/admin/user/preference`, { key, value })
        .subscribe({
          next: (response: any) => resolve(response),
          error: (error: any) => reject(error),
        });
    });
  }

  getUserPreference(key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .get(`${SETTINGS.BASE_API}/admin/user/preference`, { params: { key } })
        .subscribe({
          next: (response: any) => resolve(response),
          error: (error: any) => reject(error),
        });
    });
  }

  sendEmailVerification(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .post(`${SETTINGS.BASE_API}/admin/send-email-verification`, payload)
        .subscribe({
          next: (response: any) => {
            resolve(response);
          },
          error: (error: any) => {
            reject(error);
          },
        });
    });
  }
}
